/**
 * useTruckLayer Hook
 *
 * Creates and manages DeckGL layers for rendering truck markers with GPU acceleration.
 * This hook encapsulates the IconLayer and TextLayer creation with proper memoization
 * to prevent unnecessary layer recreation on each render.
 *
 * Key features:
 * - GPU-accelerated rendering via DeckGL IconLayer
 * - Text labels via TextLayer with outline for visibility
 * - Picking support for hover/click interactions
 * - Proper memoization for performance
 */

import { PickingInfo } from '@deck.gl/core';
import { CollisionFilterExtension } from '@deck.gl/extensions';
import { IconLayer, TextLayer } from '@deck.gl/layers';
import { useCallback, useMemo } from 'react';

import {
  ACTIVE_TRUCK_ICON_SIZE,
  DEFAULT_TRUCK_ICON_SIZE,
  TRUCK_ICON_ATLAS,
  TRUCK_ICON_MAPPING,
  TruckIconType,
} from '~components/Maps/v2/truckIconAtlas';
import { TruckLayerDatum } from '~components/Maps/v2/utils/transformTruckData';

/**
 * Information returned when a truck is picked (hovered or clicked).
 */
export interface TruckPickInfo {
  /** The truck data that was picked */
  truck: TruckLayerDatum;
  /** Screen X coordinate of the pick event */
  x: number;
  /** Screen Y coordinate of the pick event */
  y: number;
}

/**
 * Options for configuring the truck layer behavior.
 */
interface UseTruckLayerOptions {
  /** Callback when a truck is hovered */
  onHover?: (info: TruckPickInfo | null) => void;
  /** Callback when a truck is clicked */
  onClick?: (info: TruckPickInfo) => void;
  /** ID of the currently active/selected marker */
  activeMarkerId?: string | null;
  /** Whether to show text labels below truck icons */
  showLabels?: boolean;
  /** Base size for truck icons in pixels */
  iconSize?: number;
}

/**
 * Return type for the useTruckLayer hook.
 */
type TruckLayer = IconLayer<TruckLayerDatum> | TextLayer<TruckLayerDatum>;

interface UseTruckLayerResult {
  /** The truck icon layer for DeckGL */
  truckIconLayer: IconLayer<TruckLayerDatum> | null;
  /** The truck labels layer for DeckGL */
  truckLabelsLayer: TextLayer<TruckLayerDatum> | null;
  /** Combined array of layers for convenience (icon, labels) */
  layers: TruckLayer[];
}

/**
 * Default icon size for truck markers.
 */
const DEFAULT_ICON_SIZE = DEFAULT_TRUCK_ICON_SIZE;

/**
 * Pixel offset for labels below the truck icon.
 * Calculated to position text below the icon center.
 */
const LABEL_PIXEL_OFFSET: [number, number] = [0, 20];

/**
 * Font settings for truck labels.
 */
const LABEL_FONT_FAMILY = 'Roboto, Arial, sans-serif';
const LABEL_FONT_SIZE = 12;
const LABEL_OUTLINE_WIDTH = 2;

/**
 * Colors for label rendering (RGBA format for DeckGL).
 */
const LABEL_COLOR: [number, number, number, number] = [255, 255, 255, 255]; // White
const LABEL_OUTLINE_COLOR: [number, number, number, number] = [0, 0, 0, 200]; // Semi-transparent black
const LABEL_BACKGROUND_COLOR: [number, number, number, number] = [0, 0, 0, 180]; // Semi-transparent black background

/**
 * Collision filtering configuration.
 * Shared collision group ensures icons and labels consider each other for overlap detection.
 * Priority values: active/selected trucks get priority 1, others get 0.
 */
const COLLISION_GROUP = 'truck-markers';
const COLLISION_PRIORITY_ACTIVE = 1;
const COLLISION_PRIORITY_DEFAULT = 0;

/**
 * Creates DeckGL layers for rendering truck markers.
 *
 * This hook creates two layers:
 * 1. IconLayer - Renders the truck arrow icons with rotation
 * 2. TextLayer - Renders the equipment labels below each truck
 *
 * Both layers support picking for hover/click interactions.
 *
 * @param truckData - Array of transformed truck data
 * @param options - Configuration options for layer behavior
 * @returns Object containing the created layers
 *
 * @example
 * ```tsx
 * const { layers } = useTruckLayer(truckData, {
 *   onHover: (info) => setHoveredTruck(info?.truck ?? null),
 *   onClick: (info) => setSelectedTruck(info.truck),
 *   showLabels: true,
 * });
 *
 * // Pass layers to MapV2's overlayLayers prop
 * <MapV2 overlayLayers={[...otherLayers, ...layers]} />
 * ```
 */
export const useTruckLayer = (
  truckData: TruckLayerDatum[],
  options: UseTruckLayerOptions = {},
): UseTruckLayerResult => {
  const {
    onHover,
    onClick,
    activeMarkerId,
    showLabels = true,
    iconSize = DEFAULT_ICON_SIZE,
  } = options;

  /**
   * Handler for DeckGL hover events.
   * Transforms the PickingInfo into our TruckPickInfo format.
   */
  const handleHover = useCallback(
    (info: PickingInfo<TruckLayerDatum>) => {
      if (!onHover) return;

      if (info.object) {
        onHover({
          truck: info.object,
          x: info.x,
          y: info.y,
        });
      } else {
        onHover(null);
      }
    },
    [onHover],
  );

  /**
   * Handler for DeckGL click events.
   * Only fires callback if an object was actually clicked.
   */
  const handleClick = useCallback(
    (info: PickingInfo<TruckLayerDatum>) => {
      if (!onClick) return;

      if (info.object) {
        onClick({
          truck: info.object,
          x: info.x,
          y: info.y,
        });
      }
    },
    [onClick],
  );

  /**
   * Creates the truck icon layer with proper memoization.
   *
   * Key features:
   * - Uses icon atlas for efficient texture-based rendering
   * - Applies bearing rotation via getAngle
   * - Supports active state with larger icon size
   * - Enables picking for hover/click interactions
   */
  const truckIconLayer = useMemo(() => {
    if (truckData.length === 0) {
      return null;
    }

    return new IconLayer<TruckLayerDatum>({
      id: 'truck-icon-layer',
      data: truckData,

      // Icon atlas configuration
      iconAtlas: TRUCK_ICON_ATLAS,
      iconMapping: TRUCK_ICON_MAPPING,

      // Position and icon accessors
      getPosition: (d: TruckLayerDatum) => d.position,
      getIcon: (d: TruckLayerDatum) => d.icon as TruckIconType,
      getSize: (d: TruckLayerDatum) =>
        d.id === activeMarkerId ? ACTIVE_TRUCK_ICON_SIZE : iconSize,

      // Rotation - DeckGL uses degrees, with 0 pointing right (east)
      // We need to adjust since our icons point up (north) by default
      getAngle: (d: TruckLayerDatum) => 360 - d.bearing, // Convert bearing to DeckGL rotation

      // Rendering options
      sizeUnits: 'pixels',
      sizeScale: 1,
      sizeMinPixels: 16,
      sizeMaxPixels: 48,
      billboard: false, // Keep icons oriented to the map, not the camera

      // Collision filtering - hide overlapping markers, prioritize active trucks
      // Extension props are added at runtime by CollisionFilterExtension
      extensions: [new CollisionFilterExtension()],
      // @ts-expect-error - collisionGroup is provided by CollisionFilterExtension
      collisionGroup: COLLISION_GROUP,
      getCollisionPriority: (d: TruckLayerDatum) =>
        d.id === activeMarkerId ? COLLISION_PRIORITY_ACTIVE : COLLISION_PRIORITY_DEFAULT,

      // Interaction settings
      pickable: true,
      onHover: handleHover,
      onClick: handleClick,

      // Performance settings
      updateTriggers: {
        getSize: activeMarkerId,
        getIcon: activeMarkerId,
        getCollisionPriority: activeMarkerId,
      },
    });
  }, [truckData, activeMarkerId, iconSize, handleHover, handleClick]);

  /**
   * Creates the truck labels layer.
   *
   * Labels are rendered below each truck icon with:
   * - White text with black outline for visibility on any background
   * - Multi-line support (equipment name + vendor name)
   * - Semi-transparent background for readability
   */
  const truckLabelsLayer = useMemo(() => {
    if (!showLabels || truckData.length === 0) {
      return null;
    }

    // Filter out moving sites from labels (they don't need labels)
    const trucksWithLabels = truckData.filter((d) => !d.isMovingSite && d.label);

    if (trucksWithLabels.length === 0) {
      return null;
    }

    return new TextLayer<TruckLayerDatum>({
      id: 'truck-labels-layer',
      data: trucksWithLabels,

      // Position and text accessors
      getPosition: (d: TruckLayerDatum) => d.position,
      getText: (d: TruckLayerDatum) => d.label,

      // Text styling
      getSize: LABEL_FONT_SIZE,
      getColor: LABEL_COLOR,
      getTextAnchor: 'middle',
      getAlignmentBaseline: 'top',
      getPixelOffset: LABEL_PIXEL_OFFSET,

      // Font settings
      fontFamily: LABEL_FONT_FAMILY,
      fontWeight: 'bold',

      // Outline for visibility
      outlineWidth: LABEL_OUTLINE_WIDTH,
      outlineColor: LABEL_OUTLINE_COLOR,

      // Background
      background: true,
      getBackgroundColor: LABEL_BACKGROUND_COLOR,
      backgroundPadding: [4, 2, 4, 2], // [top, right, bottom, left]

      // Size constraints
      sizeUnits: 'pixels',
      sizeMinPixels: 10,
      sizeMaxPixels: 14,

      // Collision filtering - hide overlapping labels, prioritize active trucks
      // Extension props are added at runtime by CollisionFilterExtension
      extensions: [new CollisionFilterExtension()],
      // @ts-expect-error - collisionGroup is provided by CollisionFilterExtension
      collisionGroup: COLLISION_GROUP,
      getCollisionPriority: (d: TruckLayerDatum) =>
        d.id === activeMarkerId ? COLLISION_PRIORITY_ACTIVE : COLLISION_PRIORITY_DEFAULT,

      // Interaction settings - labels are pickable for hover/click
      pickable: true,
      onHover: handleHover,
      onClick: handleClick,

      // Rendering options
      billboard: true, // Always face the camera for readability
      characterSet: 'auto', // Automatically detect characters needed

      // Performance settings
      updateTriggers: {
        getText: [truckData], // Re-evaluate when data changes
        getCollisionPriority: activeMarkerId, // Re-evaluate priority when active marker changes
      },
    });
  }, [truckData, showLabels, activeMarkerId, handleHover, handleClick]);

  /**
   * Combined layers array for easy consumption.
   * Filters out null layers and returns a flat array.
   */
  const layers = useMemo(() => {
    const result: TruckLayer[] = [];

    if (truckIconLayer) result.push(truckIconLayer);
    if (truckLabelsLayer) result.push(truckLabelsLayer);

    return result;
  }, [truckIconLayer, truckLabelsLayer]);

  return {
    truckIconLayer,
    truckLabelsLayer,
    layers,
  };
};
